import React, { useEffect, useState } from 'react';
import { Alert, Button, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, TextField, Typography } from '@mui/material';
import ResponsiveDialog from '../common/ResponsiveDialog';
import type { CostType, EstimateLine } from '../../types/e2i';
import { formatAmount, lineAmount, numberText, parseNumber } from '../../utils/e2i';

interface Props {
  open: boolean;
  line: EstimateLine | null;
  costTypes: CostType[];
  defaultCostType: string;
  currency: string;
  onClose: () => void;
  onSave: (line: Omit<EstimateLine, 'lineNum'>) => void;
}

/** One line of the bill of quantities: cost type, what, how many, at what cost. */
const EstimateLineDialog: React.FC<Props> = ({ open, line, costTypes, defaultCostType, currency, onClose, onSave }) => {
  const [costType, setCostType] = useState('');
  const [itemCode, setItemCode] = useState('');
  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [uom, setUom] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [tried, setTried] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCostType(line?.costType || defaultCostType || costTypes[0]?.code || '');
    setItemCode(line?.itemCode || '');
    setDescription(line?.description || '');
    setQuantity(line ? numberText(line.quantity) : '1');
    setUom(line?.uom || '');
    setUnitCost(line ? numberText(line.unitCost) : '');
    setTried(false);
    // Only when the dialog opens or shows another line: typing must not be reset by the page re-rendering.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, line]);

  const qty = parseNumber(quantity);
  const cost = parseNumber(unitCost);
  const problems: string[] = [];
  if (!costType) problems.push('Choose the cost type');
  if (!description.trim()) problems.push('Type the description');
  if (qty === null || qty <= 0) problems.push('The quantity must be above 0');
  if (cost === null || cost < 0) problems.push('The unit cost must be 0 or more');

  const save = () => {
    setTried(true);
    if (problems.length || qty === null || cost === null) return;
    onSave({
      costType,
      itemCode: itemCode.trim() || null,
      description: description.trim(),
      quantity: qty,
      uom: uom.trim() || null,
      unitCost: cost,
      amount: lineAmount(qty, cost),
    });
  };

  const knownCostTypes = costType && !costTypes.some((c) => c.code === costType) ? [...costTypes, { code: costType, name: costType, markup: 0 }] : costTypes;

  return (
    <ResponsiveDialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="e2i-est-line-title">
      <DialogTitle id="e2i-est-line-title">{line ? `Estimate line ${line.lineNum}` : 'Add an estimate line'}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField id="e2i-est-line-type" select label="Cost type" value={costType} onChange={(e) => setCostType(e.target.value)} fullWidth required>
              {knownCostTypes.map((c) => <MenuItem key={c.code} value={c.code}>{c.name} ({c.code})</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField id="e2i-est-line-item" label="Item code (optional)" value={itemCode} onChange={(e) => setItemCode(e.target.value.slice(0, 50))} fullWidth />
          </Grid>
          <Grid item xs={12}>
            <TextField id="e2i-est-line-desc" label="Description" value={description} onChange={(e) => setDescription(e.target.value.slice(0, 254))} fullWidth required multiline maxRows={3} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField id="e2i-est-line-qty" label="Quantity" value={quantity} onChange={(e) => setQuantity(e.target.value)} inputProps={{ inputMode: 'decimal' }} fullWidth required />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField id="e2i-est-line-uom" label="Unit" value={uom} onChange={(e) => setUom(e.target.value.slice(0, 30))} placeholder="e.g. hour, m², each" fullWidth />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField id="e2i-est-line-cost" label={`Unit cost ${currency}`.trim()} value={unitCost} onChange={(e) => setUnitCost(e.target.value)} inputProps={{ inputMode: 'decimal' }} fullWidth required />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2">Amount: <strong>{formatAmount(lineAmount(qty ?? 0, cost ?? 0))} {currency}</strong></Typography>
          </Grid>
        </Grid>
        {tried && problems.length ? <Alert severity="warning" sx={{ mt: 2 }}>{problems.join('; ')}.</Alert> : null}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>Cancel</Button>
        <Button onClick={save}>{line ? 'Apply' : 'Add line'}</Button>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default EstimateLineDialog;
